import AuthObject from "../user/objects/BCPAuthObject";
import { globalVariables } from "../../global/GlobalVariables";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import { datadogLogger } from "./DatadogLogger";

// This class ensures that only one token refresh operation happens at a time
class TokenRefreshQueue {
  constructor() {
    this.isRefreshing = false;
    this.refreshPromise = null;
    this.waitingQueue = [];
  }

  // Refreshes tokens using a queue to prevent multiple simultaneous refresh operations
  async refreshTokens() {
    // If already refreshing, return the existing promise
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;

    // Create the refresh promise
    this.refreshPromise = this._performTokenRefresh()
      .then((result) => {
        return result;
      })
      .catch((error) => {
        throw error;
      })
      .finally(() => {
        this.isRefreshing = false;
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  }

  // Private method that performs the actual token refresh
  async _performTokenRefresh() {
    try {
      const authObject = new AuthObject();
      const globalVars = globalVariables();
      authObject.initialToken();

      if (
        authObject?.refreshTokenString &&
        authObject?.refreshTokenString !== "undefined"
      ) {
        const customUrl = getEnvironmentVariable("API");

        // Use a direct fetch call to avoid circular dependency with postData
        const response = await fetch(`${customUrl}/oauth/token`, {
          method: "POST",
          mode: "cors",
          cache: "default",
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
            "client-id": globalVars?.amplify?.userPoolWebClientId
          },
          redirect: "follow",
          referrerPolicy: "no-referrer",
          body: JSON.stringify({
            refresh_token: authObject.refreshTokenString,
            grant_type: "refresh_token"
          })
        });

        if (response?.ok) {
          const apiRequest = await response.json();
          authObject.syncTokens({
            accessTokenString: apiRequest?.accessToken,
            idTokenString: apiRequest?.idToken,
            refreshTokenString: apiRequest?.refreshToken
          });

          // Return success
          return { success: true };
        } else {
          const errorData = await response.json();
          throw new Error(
            `Token refresh failed: ${response.status} - ${errorData?.error || "Unknown error"}`
          );
        }
      } else {
        datadogLogger.logForceLogout({
          reason: "refresh_token_missing",
          trigger: "background_token_refresh",
          component: "TokenRefreshQueue",
          sessionCorrupted: true
        });
        authObject.removeTokens();
        throw new Error("No refresh token available");
      }
    } catch (err) {
      datadogLogger.logForceLogout({
        reason: "token_refresh_failed",
        trigger: "background_token_refresh",
        error: err?.message,
        component: "TokenRefreshQueue"
      });

      // Clean up tokens on failure
      const authObject = new AuthObject();
      authObject.removeTokens();

      throw err;
    }
  }

  // Check if a refresh operation is currently in progress
  isCurrentlyRefreshing() {
    return this.isRefreshing;
  }

  // Resets the queue state
  reset() {
    this.isRefreshing = false;
    this.refreshPromise = null;
    this.waitingQueue = [];
  }
}

// Create a singleton instance
const tokenRefreshQueue = new TokenRefreshQueue();

export default tokenRefreshQueue;
